<template>
    <template v-if="role">
        <div class="row align-items-end mb-3">
            <div class="col-sm mb-2 mb-sm-0">
                <h1 class="page-header-title">{{ formatRoleName() }} ({{ role?.permissions?.length}} {{ permissionCountLabel }})</h1>
            </div>
        </div>
        <div class="row">
            <div v-if="groupedPermissions" class="d-flex flex-wrap">
                <div v-for="(permissions, category) in groupedPermissions" :key="category" class="category-card mb-3">
                    <div class="card m-3 h-100">
                        <div class="card-body">
                            <h5 class="card-title category-name">{{ category }}</h5>
                            <ul class="list-group list-group-flush">
                                <li v-for="permission in permissions" :key="permission.id" class="list-group-item"
                                    style="padding: 0.5rem;">
                                    {{ permission.name }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </template>
    <template v-else>
        <AppLoading :loading="true" />
    </template>

</template>

<script setup lang="ts">

import { useRoleStore } from "@/vue/store/roleStore";
import { computed, ComputedRef, onMounted, onUnmounted } from "vue";
import { Role } from "@/vue/types/role";
import AppLoading from "@/vue/shared/AppLoading.vue";

const props = defineProps<{ roleId: number }>();

const roleStore = useRoleStore();
const role: ComputedRef<Role | null> = computed(() => roleStore.entity);

const categories = [
    "accessory",
    "announcements",
    "api.token",
    "benefits.managers",
    "bulk_usage_upload",
    "dashboard",
    "device",
    "followUp.type",
    "followUp",
    "migrate",
    "maskModel",
    "maskManufacturer",
    "mobile.notification",
    "org.location",
    "org.group",
    "org.settings",
    "org",
    "patient.note",
    "patient",
    "physician",
    "role",
    "staff",
    "trusted-domain",
    "user.security",
    "user"
];

const groupedPermissions = computed(() => {
    if (!role.value) return null;

    const groups: Record<string, any[]> = {};

    role.value.permissions?.forEach(permission => {
        let assigned = false;
        for (const category of categories) {
            if (permission.name.includes(category)) {
                let name = category.replace(/[._]/g, " ");

                if (!groups[name]) {
                    groups[name] = [];
                }

                groups[name].push(permission);
                assigned = true;

                break;
            }
        }

        if (!assigned) {
            if (!groups["Uncategorized"]) {
                groups["Uncategorized"] = [];
            }

            groups["Uncategorized"].push(permission);
        }
    });

    for (const category in groups) {
        groups[category].sort((a, b) => a.name.localeCompare(b.name));
    }

    const sortedGroupsArray = Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));

    return Object.fromEntries(sortedGroupsArray);
});

const formatRoleName = () => {
    if (!role?.value?.name) return '';

    // Split by dots and other separators
    const parts = role.value.name.split(/[._]/);

    // Special handling for benefit manager and third party roles
     if (role.value.name.includes('benefit.manager') || role.value.name.includes('third.party')) {
        return parts
            .map(part => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');
    }

    // Special handling for clinical and ep roles
    if ((parts.includes('clinical') || parts.includes('ep'))) {
        // Create a new array to store the reordered parts
        const reorderedParts = [...parts];

        // Find org, location, group, clinical, and ep indices
        const orgIndex = reorderedParts.indexOf('org');
        const locationIndex = reorderedParts.indexOf('location');
        const groupIndex = reorderedParts.indexOf('group');
        const clinicalIndex = reorderedParts.indexOf('clinical');
        const epIndex = reorderedParts.indexOf('ep');

        // If both org and clinical exist, move clinical before org
        if (orgIndex !== -1 && clinicalIndex !== -1) {
            // Remove clinical
            reorderedParts.splice(clinicalIndex, 1);
            // Insert it before org
            reorderedParts.splice(orgIndex, 0, 'clinical');
        }

        // If both org and ep exist, move ep before org
        if (orgIndex !== -1 && epIndex !== -1) {
            // Remove ep (account for shift if clinical was already moved)
            const currentEpIndex = reorderedParts.indexOf('ep');
            reorderedParts.splice(currentEpIndex, 1);
            // Insert it before org
            const currentOrgIndex = reorderedParts.indexOf('org');
            reorderedParts.splice(currentOrgIndex, 0, 'ep');
        }

        // If both location and ep exist, move ep before location
        if (locationIndex !== -1 && epIndex !== -1) {
            // Remove ep (account for shift if clinical was already moved)
            const currentEpIndex = reorderedParts.indexOf('ep');
            reorderedParts.splice(currentEpIndex, 1);
            // Insert it before location
            const currentLocationIndex = reorderedParts.indexOf('location');
            reorderedParts.splice(currentLocationIndex, 0, 'ep');
        }

        // If both group and ep exist, move ep before group
        if (groupIndex !== -1 && epIndex !== -1) {
            // Remove ep (account for shift if clinical was already moved)
            const currentEpIndex = reorderedParts.indexOf('ep');
            reorderedParts.splice(currentEpIndex, 1);
            // Insert it before group
            const currentGroupIndex = reorderedParts.indexOf('group');
            reorderedParts.splice(currentGroupIndex, 0, 'ep');
        }

        // Format all parts
        return reorderedParts
            .map(part => {
                if (part === 'ep') return 'EP';
                return part.charAt(0).toUpperCase() + part.slice(1);
            })
            .join(' ');
    }

    // Special handling for view roles
    if (parts.includes('view') || role.value.name.includes('view.only')) {
        return parts
            .map(part => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');
    }

    // Default case - just take first and last parts
    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    } else {
        const firstPart = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
        const lastPart = parts[parts.length - 1].charAt(0).toUpperCase() + parts[parts.length - 1].slice(1);
        return `${firstPart} ${lastPart}`;
    }
};

const permissionCountLabel = computed(() => {
    if (!role.value) return null;

    return role.value.permissions?.length == 1 ? "permission" : "permissions";

});

onMounted(async () => {
    await roleStore.get(props.roleId, {
        with: ["permissions"]
    });
});

onUnmounted(() => {
    roleStore.clean();
})

</script>

<style scoped>
.category-card {
    flex: 1 1 25%;
    max-width: 25%;
    display: flex;
    flex-direction: column;
}

.category-name::first-letter {
    text-transform: uppercase;
}

@media (max-width: 768px) {
    .category-card {
        flex: 1 1 100%;
        max-width: 100%;
    }
}
</style>
